import { Two048 } from '@minigame-race/contracts';
import { useEffect, useRef } from 'react';
import { color } from '../../theme.js';
import type { MinigameProps } from '../../race/types.js';

/**
 * 2048-to-Target. Arrow keys or swipe to slide. The board is authoritative from
 * the server; we emit a direction and render whatever comes back. No optimistic
 * prediction — a slide+merge+spawn is too much to mispredict cleanly, and the
 * round-trip is a single tick.
 */

// Tile colour ramps with value — brighter/greener as you climb toward target.
const TILE_BG: Record<number, string> = {
  2: '#1c2417', 4: '#243016', 8: '#31421c', 16: '#3e5620',
  32: '#4d6b22', 64: '#5f8524', 128: '#74a026', 256: '#8abd28',
  512: '#a6ff2e', 1024: '#c6ff5e', 2048: '#e4ff9a',
};
const tileText = (v: number): string => (v <= 32 ? 'rgba(234,255,234,.85)' : '#08110a');

function isView(v: unknown): v is Two048.Two048PrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'two048';
}

export function Two048Game({ view, emit }: MinigameProps) {
  const s = isView(view) ? view : null;
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const map: Record<string, Two048.Two048Direction> = {
        ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
        w: 'up', s: 'down', a: 'left', d: 'right',
      };
      const dir = map[e.key];
      if (dir) { e.preventDefault(); emit({ type: 'move', dir }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [emit]);

  if (!s) return null;

  const onTouchStart = (e: React.PointerEvent): void => { touchStart.current = { x: e.clientX, y: e.clientY }; };
  const onTouchEnd = (e: React.PointerEvent): void => {
    const st = touchStart.current;
    touchStart.current = null;
    if (!st) return;
    const dx = e.clientX - st.x, dy = e.clientY - st.y;
    if (Math.abs(dx) < 24 && Math.abs(dy) < 24) return; // too small; ignore tap
    const dir: Two048.Two048Direction =
      Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
    emit({ type: 'move', dir });
  };

  return (
    <div style={{ width: '100%', maxWidth: 'min(84cqmin,400px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'clamp(10px,2.2cqmin,16px)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 14, fontFamily: "'JetBrains Mono',monospace" }}>
        <span style={{ fontSize: 'clamp(9px,1.7cqmin,12px)', letterSpacing: '.16em', color: 'rgba(234,255,234,.45)' }}>REACH</span>
        <span style={{ fontSize: 'clamp(20px,4.4cqmin,32px)', fontWeight: 800, color: color.lime, lineHeight: 1 }}>{s.target}</span>
        <span style={{ fontSize: 'clamp(9px,1.7cqmin,12px)', letterSpacing: '.14em', color: 'rgba(234,255,234,.4)' }}>BEST {s.best}</span>
      </div>

      <div
        onPointerDown={onTouchStart}
        onPointerUp={onTouchEnd}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${s.size}, 1fr)`,
          gap: 'clamp(4px,1.2cqmin,8px)',
          width: '100%',
          aspectRatio: '1 / 1',
          padding: 'clamp(4px,1.2cqmin,8px)',
          borderRadius: 12,
          background: 'rgba(0,0,0,.4)',
          border: '1px solid rgba(166,255,46,.14)',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {s.cells.map((v, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              placeItems: 'center',
              borderRadius: 8,
              background: v === 0 ? 'rgba(255,255,255,.03)' : (TILE_BG[v] ?? '#e4ff9a'),
              fontFamily: "'JetBrains Mono',monospace",
              fontWeight: 800,
              fontSize: v >= 1000 ? 'clamp(13px,3.4cqmin,24px)' : 'clamp(16px,4.4cqmin,30px)',
              color: v === 0 ? 'transparent' : tileText(v),
              transition: 'background .12s ease',
            }}
          >
            {v || ''}
          </div>
        ))}
      </div>

      {s.stuck && !s.solved ? (
        <div style={{ textAlign: 'center', fontSize: 'clamp(11px,2.2cqmin,15px)', color: color.redBright, fontWeight: 700 }}>
          No moves left
        </div>
      ) : (
        <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', color: 'rgba(234,255,234,.4)' }}>
          Swipe or use arrow keys. Merge up to {s.target}.
        </div>
      )}
    </div>
  );
}
